/**
 * 사업장 목록 / 등록 / 수정
 * ---------------------------------------------------------------------------
 * 목록은 개인정보를 받지 않는다(서버가 내려보내지 않는다). 수정 화면을 열 때만
 * 상세를 다시 불러 복호화된 값을 채운다.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  App as AntApp,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { Site, SiteDeleteCheck, SiteInput } from '@eco/shared';
import { ECO_PERMISSIONS } from '@eco/shared';
import { api, errorMessage } from '../lib/api';
import { downloadCsv } from '../lib/csv';
import { can, useSession } from '../lib/session';
import { HelpH, HelpList, HelpP, HelpPath, PageHelp } from '../components/PageHelp';
import { SiteApproverDialog } from './site/SiteApproverDialog';

export function SiteListPage() {
  const { message, modal } = AntApp.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const manage = can(session, ECO_PERMISSIONS.SITE_MANAGE);

  const [editing, setEditing] = useState<Site | 'new' | null>(null);
  const [approverFor, setApproverFor] = useState<Site | null>(null);
  const [form] = Form.useForm<SiteInput>();

  const sites = useQuery<Site[]>({
    queryKey: ['sites'],
    queryFn: async () => (await api.get<Site[]>('/sites', { params: { includeInactive: true } })).data,
  });

  // 수정을 누르면 상세를 다시 부른다. 목록에는 담당자명이 없기 때문이다.
  const detail = useQuery<Site>({
    queryKey: ['site', editing !== 'new' && editing ? editing.siteId : null],
    queryFn: async () =>
      (await api.get<Site>(`/sites/${(editing as Site).siteId}`)).data,
    enabled: editing !== null && editing !== 'new',
  });

  useEffect(() => {
    if (editing === 'new') {
      form.resetFields();

      /*
       * 새 사업장은 회사 정보로 시작한다.
       *
       * 사업장은 대개 본사와 상호·사업자번호·업태·주소·전화가 같다. 플랫폼이
       * 이미 들고 있는 값을 다시 적게 할 이유가 없다. 다른 사업장이면 그
       * 자리에서 고치면 된다 — 채워 둘 뿐 잠그지 않는다.
       *
       * 사업장명도 회사 이름으로 채워 둔다. 사업장이 하나인 기업이 대부분이고,
       * 그 경우 회사 이름이 곧 사업장 이름이다. 여러 곳이면 '○○ 2공장' 처럼
       * 뒤를 고치는 편이 처음부터 전부 적는 것보다 빠르다.
       *
       * 사업장 코드만 비워 둔다. 사람이 정하는 값이고, 회사 이름을 그대로
       * 넣으면 사업장이 둘이 되는 순간 코드가 겹친다.
       */
      const c = session?.company;
      if (c) {
        form.setFieldsValue({
          siteName: c.companyName || undefined,
          ceoName: c.ceoName ?? undefined,
          bizRegNo: c.bizRegNo ?? undefined,
          bizType: c.bizType ?? undefined,
          bizItem: c.bizItem ?? undefined,
          zipCode: c.zipCode ?? undefined,
          address1: c.address1 ?? undefined,
          address2: c.address2 ?? undefined,
          contactTel: c.companyTel ?? undefined,
        } as Partial<SiteInput>);
      }
    } else if (detail.data) {
      form.setFieldsValue(detail.data);
    }
  }, [editing, detail.data, form, session]);

  const save = useMutation({
    mutationFn: async (values: SiteInput) => {
      if (editing === 'new') return api.post('/sites', values);
      return api.patch(`/sites/${(editing as Site).siteId}`, values);
    },
    onSuccess: async () => {
      message.success(editing === 'new' ? '사업장을 등록했습니다.' : '수정했습니다.');
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
    onError: (err) => message.error(errorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: async (siteId: string) => api.delete(`/sites/${siteId}`),
    onSuccess: async () => {
      message.success('사업장을 삭제했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
    onError: (err) => message.error(errorMessage(err)),
  });

  /**
   * 지우기 전에 무엇이 걸려 있는지 먼저 묻는다.
   *
   * 그냥 "삭제하시겠습니까?" 만 띄우면, 일지 300건이 달린 사업장인지
   * 어제 잘못 만든 빈 사업장인지 구분이 안 된 채 누르게 된다.
   */
  const askRemove = async (row: Site) => {
    let check: SiteDeleteCheck;
    try {
      check = (await api.get<SiteDeleteCheck>(`/sites/${row.siteId}/delete-check`)).data;
    } catch (err) {
      message.error(errorMessage(err));
      return;
    }

    if (!check.deletable) {
      modal.error({
        title: `'${row.siteName}' 은(는) 삭제할 수 없습니다`,
        width: 520,
        content: (
          <div>
            <p style={{ marginTop: 8 }}>아래 기록이 남아 있습니다.</p>
            <ul style={{ margin: '8px 0 12px 20px' }}>
              {check.blockers.map((b) => (
                <li key={b.label}>
                  {b.label} <b>{b.count.toLocaleString()}건</b>
                </li>
              ))}
            </ul>
            <p style={{ marginBottom: 0 }}>
              보존 기간이 있는 기록이라 지우지 않습니다. 더 쓰지 않는 사업장이라면{' '}
              <b>수정 → 「운영 중」을 끄면</b> 폐쇄 상태가 되고, 일지는 그대로 남습니다.
            </p>
          </div>
        ),
        okText: '알겠습니다',
      });
      return;
    }

    modal.confirm({
      title: `'${row.siteName}' 을(를) 삭제할까요?`,
      width: 520,
      okText: '삭제',
      okButtonProps: { danger: true },
      cancelText: '취소',
      content: (
        <div>
          {check.cascades.length > 0 ? (
            <>
              <p style={{ marginTop: 8 }}>함께 지워집니다.</p>
              <ul style={{ margin: '8px 0 12px 20px' }}>
                {check.cascades.map((c) => (
                  <li key={c.label}>
                    {c.label} <b>{c.count.toLocaleString()}건</b>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p style={{ marginTop: 8 }}>이 사업장에는 딸린 자료가 없습니다.</p>
          )}
          <p style={{ marginBottom: 0 }}>되돌릴 수 없습니다.</p>
        </div>
      ),
      onOk: () => remove.mutateAsync(row.siteId),
    });
  };

  return (
    <Card
      title="사업장"
      extra={
        <Space>
          <PageHelp title="사업장 — 화면 설명" withLabel>
            <HelpP>
              사업장은 <b>일지와 시설이 매달리는 단위</b>입니다. 여기에 등록된 사업장이
              일지 작성 화면의 「사업장」 목록에 나타나고, 법정 서식을 인쇄할 때
              사업장명·대표자·허가번호가 그대로 찍힙니다.
            </HelpP>

            <HelpH>회사 정보는 미리 채워집니다</HelpH>
            <HelpP>
              사업장을 새로 만들면 상호·대표자·사업자번호·업태·종목·주소·전화가{' '}
              <b>WORK&amp;Hub 에 등록된 회사 정보로 미리 채워집니다.</b> 본사 사업장이면
              그대로 저장하면 되고, 공장처럼 다른 곳이면 고쳐서 쓰면 됩니다. 빈칸으로
              나온다면 <HelpPath>WORK&amp;Hub → 기업 설정</HelpPath> 에 그 값이 아직 없는
              것입니다. 사업장 코드만은 겹치면 안 되므로 직접 정해 주세요.
            </HelpP>

            <HelpH>처음이라면 이 순서로 하세요</HelpH>
            <HelpList
              items={[
                {
                  term: '1. 사업장 등록',
                  desc: '상호·허가번호·주소를 넣습니다. 회사 정보는 미리 채워져 있습니다.',
                },
                {
                  term: '2. 사업장 시설관리',
                  desc: (
                    <>
                      배출시설·방지시설을 등록합니다. <b>이걸 해야 일지에 시설 행이
                      자동으로 생깁니다.</b>
                    </>
                  ),
                },
                {
                  term: '3. 일지 기본 확인자',
                  desc: '결재란에 들어갈 사람을 미리 정해 둡니다. 매일 고르지 않아도 됩니다.',
                },
              ]}
            />

            <HelpH>여기 넣은 값이 어디에 쓰이나</HelpH>
            <HelpList
              items={[
                { term: '사업장명·대표자·허가번호', desc: '법정 서식 인쇄물의 머리말' },
                { term: '주소', desc: '일지의 날씨를 자동으로 받아올 좌표 기준' },
                {
                  term: '일지 작성 시 시설 행 자동 생성',
                  desc: '켜 두면 일지를 열 때 등록된 시설이 표에 미리 채워집니다.',
                },
              ]}
            />

            <HelpH>사업장 수는 요금에 들어갑니다</HelpH>
            <HelpP>
              요금제에 <b>포함된 사업장 수</b>가 정해져 있고, 그보다 많이 등록하면 넘은
              만큼 다음 청구서에 더해집니다. 지금 몇 곳까지 쓸 수 있는지는{' '}
              <HelpPath>WORK&amp;Hub → 구독 → 사용량·청구</HelpPath> 의 「사업장 수」 칸에
              나옵니다. 세는 것은 <b>운영 중인 사업장</b>뿐이라, 폐쇄하면 다음 집계부터
              수에서 빠집니다(시설도 같습니다).
            </HelpP>

            <HelpH>폐쇄와 삭제는 다릅니다</HelpH>
            <HelpP>
              <b>폐쇄</b>(수정 → 「운영 중」 끄기)는 행을 남깁니다. 일지는 그대로 보존됩니다.
              <br />
              <b>삭제</b>는 행을 지웁니다. 일지가 하나라도 있으면 삭제되지 않습니다 —
              법정 보존 기간이 있기 때문입니다. 잘못 만든 사업장을 치울 때만 쓰세요.
            </HelpP>
          </PageHelp>

          {/*
            내려받기는 권한을 따지지 않는다. 이 화면을 볼 수 있다는 것은
            이미 목록을 보고 있다는 뜻이라, 같은 내용을 파일로 받는 것을
            막을 이유가 없다. 서비스를 그만두더라도 자기 자료는 가져갈 수
            있어야 한다(클라우드컴퓨팅법 제27조).
          */}
          <Button
            icon={<DownloadOutlined />}
            disabled={(sites.data ?? []).length === 0}
            onClick={() =>
              downloadCsv('사업장', sites.data ?? [], [
                { header: '코드', value: (r) => r.siteCode },
                { header: '사업장명', value: (r) => r.siteName },
                { header: '종별', value: (r) => (r.siteGrade ? `${r.siteGrade}종` : '') },
                { header: '허가번호', value: (r) => r.permitNo },
                { header: '우편번호', value: (r) => r.zipCode },
                { header: '주소', value: (r) => r.address1 },
                { header: '상세주소', value: (r) => r.address2 },
                { header: '상태', value: (r) => (r.isActive ? '운영중' : '폐쇄') },
              ])
            }
          >
            엑셀 내려받기
          </Button>

          {manage && (
            <Button type="primary" onClick={() => setEditing('new')}>
              사업장 등록
            </Button>
          )}
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="일지에 시설이 나오지 않는다면 여기서 먼저 등록하세요."
        description={
          <>
            대기 운영기록부·폐수 자가운영일지의 <b>배출시설·오염방지시설</b> 표는 각
            사업장의 <HelpPath>사업장 시설관리</HelpPath> 에 등록된 시설로 채워집니다.
            시설을 등록하지 않으면 일지의 표가 비어 있습니다.
          </>
        }
      />
      <Table<Site>
        rowKey="siteId"
        loading={sites.isLoading}
        dataSource={sites.data ?? []}
        pagination={false}
        columns={[
          { title: '코드', dataIndex: 'siteCode', width: 120 },
          { title: '사업장명', dataIndex: 'siteName' },
          {
            title: '종별',
            dataIndex: 'siteGrade',
            width: 80,
            render: (v: string | null) => (v ? `${v}종` : '-'),
          },
          { title: '허가번호', dataIndex: 'permitNo', width: 160, render: (v) => v ?? '-' },
          {
            title: '주소',
            dataIndex: 'address1',
            render: (_, row) => [row.address1, row.address2].filter(Boolean).join(' ') || '-',
          },
          {
            title: '상태',
            dataIndex: 'isActive',
            width: 90,
            render: (active: boolean) =>
              active ? <Tag color="green">운영중</Tag> : <Tag>폐쇄</Tag>,
          },
          {
            title: '',
            width: 330,
            render: (_, row) => (
              <Space>
                <Button size="small" onClick={() => navigate(`/sites/${row.siteId}/facilities`)}>
                  사업장 시설관리
                </Button>
                {/*
                  일지를 새로 만들 때 결재란에 들어갈 사람들이다. 여기서
                  등록해 두면 매일 다시 고르지 않아도 된다.
                */}
                <Button size="small" onClick={() => setApproverFor(row)}>
                  일지 기본 확인자
                </Button>
                {manage && (
                  <>
                    <Button size="small" type="link" onClick={() => setEditing(row)}>
                      수정
                    </Button>
                    <Button
                      size="small"
                      type="link"
                      danger
                      loading={remove.isPending}
                      onClick={() => void askRemove(row)}
                    >
                      삭제
                    </Button>
                  </>
                )}
              </Space>
            ),
          },
        ]}
      />

      <SiteApproverDialog
        open={approverFor !== null}
        siteId={approverFor?.siteId ?? null}
        siteName={approverFor?.siteName ?? ''}
        readOnly={!manage}
        onClose={() => setApproverFor(null)}
      />

      <Modal
        open={editing !== null}
        title={
          <Space>
            <span>{editing === 'new' ? '사업장 등록' : '사업장 수정'}</span>
            <PageHelp title="사업장 등록 — 항목 설명">
              <HelpP>
                별표가 없는 항목은 비워 두어도 저장됩니다. 다만 아래 항목은 법정 서식에
                그대로 인쇄되므로, 서식을 출력할 계획이라면 채워 두는 편이 좋습니다.
              </HelpP>
              <HelpList
                items={[
                  { term: '사업장 코드', desc: '기업 안에서만 쓰는 구분자입니다. 중복될 수 없습니다.' },
                  { term: '사업장명', desc: '서식 머리말의 「사업장명」 칸에 찍힙니다.' },
                  { term: '배출시설 허가(신고)번호', desc: '서식의 「허가(신고)번호」 칸입니다.' },
                  { term: '종별', desc: '대기·수질 사업장 종별(1~5종). 모르면 비워 두세요.' },
                  {
                    term: '주소',
                    desc: '일지의 날씨를 이 주소 기준으로 받아옵니다. 넣어 두면 매일 날씨를 손으로 적지 않아도 됩니다.',
                  },
                  {
                    term: '대표자 · 관리책임자 · 환경기술인 · 연락처 · 이메일',
                    desc: '개인정보입니다. 저장할 때 암호화되며 목록 화면에는 나타나지 않습니다.',
                  },
                  {
                    term: '일지 작성 시 시설 행 자동 생성',
                    desc: '켜 두면 일지를 열 때 등록된 시설이 표에 미리 채워집니다. 대부분 켜 두는 편이 낫습니다.',
                  },
                ]}
              />
            </PageHelp>
          </Space>
        }
        onCancel={() => setEditing(null)}
        onOk={() => form.submit()}
        confirmLoading={save.isPending}
        okText="저장"
        cancelText="취소"
        width={640}
        destroyOnClose
      >
        <Form<SiteInput>
          form={form}
          layout="vertical"
          onFinish={(values) => save.mutate(values)}
          style={{ marginTop: 16 }}
        >
          <Space.Compact style={{ width: '100%', gap: 12 }}>
            <Form.Item
              name="siteCode"
              label="사업장 코드"
              rules={[{ required: true, message: '사업장 코드를 입력하세요' }]}
              style={{ flex: 1 }}
            >
              <Input maxLength={40} placeholder="예: HQ01" />
            </Form.Item>
            <Form.Item
              name="siteName"
              label="사업장명"
              rules={[{ required: true, message: '사업장명을 입력하세요' }]}
              style={{ flex: 2 }}
            >
              <Input maxLength={200} />
            </Form.Item>
          </Space.Compact>

          <Space.Compact style={{ width: '100%', gap: 12 }}>
            <Form.Item name="bizRegNo" label="사업자등록번호" style={{ flex: 1 }}>
              <Input maxLength={10} placeholder="숫자 10자리" />
            </Form.Item>
            <Form.Item name="permitNo" label="배출시설 허가(신고)번호" style={{ flex: 1 }}>
              <Input maxLength={60} />
            </Form.Item>
            <Form.Item name="siteGrade" label="종별" style={{ width: 120 }}>
              <Select
                allowClear
                options={['1', '2', '3', '4', '5'].map((g) => ({ value: g, label: `${g}종` }))}
              />
            </Form.Item>
          </Space.Compact>

          <Form.Item
            name="address1"
            label="주소"
            extra="일지의 날씨를 이 주소 기준으로 받아옵니다."
          >
            <Input maxLength={300} />
          </Form.Item>
          <Form.Item name="address2" label="상세 주소">
            <Input maxLength={300} />
          </Form.Item>

          <Space.Compact style={{ width: '100%', gap: 12 }}>
            <Form.Item name="bizType" label="업태" style={{ flex: 1 }}>
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item name="bizItem" label="종목" style={{ flex: 1 }}>
              <Input maxLength={100} />
            </Form.Item>
          </Space.Compact>

          {/* 아래는 개인정보다. 저장 시 암호화되며 목록에는 나타나지 않는다. */}
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="아래 네 항목은 개인정보입니다. 저장할 때 암호화되고 목록 화면에는 표시되지 않습니다."
          />
          <Space.Compact style={{ width: '100%', gap: 12 }}>
            <Form.Item name="ceoName" label="대표자" style={{ flex: 1 }}>
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item name="managerName" label="관리책임자" style={{ flex: 1 }}>
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item name="managerPosition" label="직위" style={{ width: 120 }}>
              <Input maxLength={60} />
            </Form.Item>
          </Space.Compact>

          <Space.Compact style={{ width: '100%', gap: 12 }}>
            <Form.Item name="technicianName" label="환경기술인" style={{ flex: 1 }}>
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item name="technicianGrade" label="기술인 등급" style={{ width: 130 }}>
              <Input maxLength={40} />
            </Form.Item>
            <Form.Item name="contactTel" label="연락처" style={{ flex: 1 }}>
              <Input maxLength={40} />
            </Form.Item>
            <Form.Item name="contactEmail" label="이메일" style={{ flex: 1 }}>
              <Input maxLength={200} />
            </Form.Item>
          </Space.Compact>

          <Form.Item
            name="autoFillFacilities"
            label="일지 작성 시 시설 행 자동 생성"
            valuePropName="checked"
            extra="끄면 빈 양식이 열립니다. 대부분은 켜 두는 편이 낫습니다."
          >
            <Switch />
          </Form.Item>

          {editing !== 'new' && (
            <Form.Item name="isActive" label="운영 중" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Card>
  );
}
